import React, { useCallback, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Check, RefreshCw, RotateCcw, Trash2 } from "lucide-react";
import { api } from "../console/api";
import { Groupe, TitreGroupe, Retour, Vide, Badge } from "../espace/ui";
import { MOIS, dateCourte, francs, iso } from "./outils";

/* Les mêmes mots et couleurs que la console (Profs → Décomptes du mois), tests du
   01.11.2026 : l'app disait « pas encore confirmé » / « confirmé par le prof ». */
const moisCourant = () => iso(new Date()).slice(0, 7);
const etatDecompte = (statut, mois, horsApp) => {
  if (statut === "valide") return ["validé", "#16A34A"];
  if (statut === "confirme") return ["envoyé · à valider", "#2563EB"];
  if (horsApp) return ["fait à la main", "#94A3B8"];
  if (mois > moisCourant()) return ["à venir", "#94A3B8"];
  return mois === moisCourant() ? ["mois en cours", "#94A3B8"] : ["pas encore envoyé", "#D97706"];
};
// Une séance sans élève (0.–) : rien à vérifier (comme profs.sans_eleve côté serveur).
const sansEleve = (l) => ["cours", "remplacement"].includes(l.type) && !l.eleves && !l.minutes && !Number(l.montant || 0);
const decalerMois = (m, n) => {
  const [a, mm] = m.split("-").map(Number);
  const d = new Date(a, mm - 1 + n, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};
const moisLisible = (m) => { const [a, mm] = m.split("-").map(Number); return `${MOIS[mm - 1]} ${a}`; };
const deMois = (m) => { const t = moisLisible(m); return /^[aeiou]/.test(t) ? `d'${t}` : `de ${t}`; };

const Decompte = ({ pid, mois, horsApp, onRetour }) => {
  const [d, setD] = useState(null);
  const [erreur, setErreur] = useState("");
  const [message, setMessage] = useState("");
  const [envoi, setEnvoi] = useState(false);
  const charger = useCallback(() => api(`/profs/${pid}/decompte/${mois}`).then(setD).catch((e) => setErreur(e.message)), [pid, mois]);
  useEffect(() => { charger(); }, [charger]);
  const agir = async (quoi) => {
    let suffixe = "";
    setErreur(""); setMessage("");
    if (quoi === "valider") {
      // Valider un mois que le prof n'a jamais envoyé : on le demande d'abord.
      if (d.statut !== "confirme" && !window.confirm(
        `${d.prof.prenom} n'a pas encore envoyé ses heures ${deMois(mois)}. Valider quand même ?`)) return;
      const douteuses = d.lignes.filter((l) => !l.retire && !sansEleve(l) && String(l.note || "").includes("à vérifier"));
      if (douteuses.length) {
        if (!window.confirm(`${douteuses.length} séance(s) d'un cours partagé sans horaire au tableau « Horaires » (comptées en entier). Valider tel quel ?`)) return;
        suffixe = "?forcer=true";
      }
      // Durées annoncées par les profs (horaires partagés) pas encore décidées : comme la console.
      if (d.propositions_en_attente) {
        if (!window.confirm(`${d.propositions_en_attente} durée(s) annoncée(s) par les profs attendent ta décision (console → Profs → Horaires partagés). Valider tel quel ?`)) return;
        suffixe = "?forcer=true";
      }
    }
    setEnvoi(true);
    try {
      await api(`/profs/${pid}/decompte/${mois}/${quoi}${suffixe}`, { method: "POST" });
      await charger();
      setMessage(quoi === "valider" ? `Validé : ${d.prof.prenom} le retrouve dans son application (onglet Paie) et le reçoit par e-mail.` : "Décompte rouvert : corrige, puis valide à nouveau.");
    } catch (e) { setErreur(e.message); }  // le décompte reste affiché, l'erreur dessous
    setEnvoi(false);
  };
  if (!d) return erreur ? <><Retour onClick={onRetour} label="Profs" /><Vide>{erreur}</Vide></>
    : <div className="py-16 grid place-items-center"><RefreshCw className="animate-spin" size={20} /></div>;
  const [lib, coul] = etatDecompte(d.statut, mois, horsApp);
  // Le mois n'est pas fini : on ne valide pas encore (le serveur refusait, l'écran
  // disparaissait derrière le message d'erreur).
  const fini = mois < moisCourant();
  const vides = d.lignes.filter(sansEleve).length;
  return (
    <div className="space-y-5" data-testid="ecole-decompte">
      <Retour onClick={onRetour} label="Profs" />
      <div>
        <div className="font-display text-[24px] leading-tight">{d.prof.nom_complet || d.prof.prenom}</div>
        <div className="text-[13px] text-[var(--muted)] mt-1 capitalize">{moisLisible(mois)}</div>
        <div className="flex items-center gap-2 mt-2">
          <Badge couleur={coul}>{lib}</Badge>
          <span className="text-[14px]">{d.heures.toFixed(2)} h · <strong>{francs(d.total)}</strong> brut</span>
        </div>
      </div>
      {/* Validé, mais une donnée a changé depuis (élève changé de cours « depuis le début ») :
          la différence se reporte depuis la console (Profs › décompte). */}
      {d.regularisation && (
        <p className="text-[13px] rounded-2xl px-4 py-3" style={{ background: "#FFFBEB", color: "#92400E" }} data-testid="ecole-decompte-regul">
          Depuis la validation : {francs(d.regularisation.montant)}. {(d.regularisation.resume || []).map((g) => g.texte).join(" · ")}. À reporter depuis la console (Profs › ce décompte).
        </p>
      )}
      <Groupe>
        {d.lignes.filter((l) => !sansEleve(l)).map((l, n) => (
          <div key={n} className="flex items-start justify-between gap-3 px-4 py-3" style={l.retire ? { opacity: 0.45 } : undefined}>
            <div className="min-w-0">
              <div className="text-[14.5px] leading-snug">{l.cours}{l.remplace ? ` (remplace ${l.remplace})` : ""}
                {l.ajout_prof && <span className="ml-1.5 align-middle"><Badge couleur="#7C3AED">ajouté par le prof</Badge></span>}</div>
              <div className="text-[12px] text-[var(--muted)]">
                {l.type === "régularisation" ? "régularisation d'un mois validé" : dateCourte(l.date)}{l.debut ? ` · ${l.debut}–${l.fin}` : ""}{l.eleves !== undefined && (l.type === "cours" || l.type === "remplacement") ? ` · ${l.eleves} élèves` : ""}{l.tarif ? ` · ${l.tarif}.–${l.tarif_par === "cours" ? "/cours" : "/h"}` : ""}
              </div>
              {/* « ajouté par le prof » : déjà dit par la pastille. */}
              {((l.note && !(l.ajout_prof && l.note === "ajouté par le prof")) || l.retire) && (
                <div className="text-[12px] text-[var(--muted)]">{l.retire ? `retiré par ${l.retire_par === "école" ? "l'école" : "le prof"}` : l.note}</div>
              )}
            </div>
            <div className="text-[14.5px] font-semibold tabular-nums shrink-0">{francs(l.retire ? 0 : l.montant)}</div>
          </div>
        ))}
        {vides > 0 && <p className="px-4 py-3 text-[12.5px] text-[var(--muted)]">+ {vides} cours sans élève (0.–, rien à vérifier)</p>}
      </Groupe>
      {d.statut !== "valide" ? (fini ? (
        <button onClick={() => agir("valider")} disabled={envoi} className="btn-primary w-full justify-center" data-testid="ecole-decompte-valider">
          <Check size={15} /> {envoi ? "Validation…" : "Valider : le prof pourra télécharger son décompte"}
        </button>
      ) : (
        <p className="text-[13px] rounded-2xl px-4 py-3" style={{ background: "#EFF6FF", color: "#1D4ED8" }} data-testid="ecole-decompte-pas-fini">
          Le mois n'est pas fini : tu valides ce décompte au début du mois suivant, une fois que {d.prof.prenom} a envoyé ses heures.
        </p>
      )) : (
        <button onClick={() => agir("rouvrir")} disabled={envoi} className="btn-ghost w-full justify-center"><RotateCcw size={14} /> Rouvrir</button>
      )}
      {message && <p className="text-sm" style={{ color: "#15803D" }}>{message}</p>}
      {erreur && <p className="text-sm" style={{ color: "#B91C1C" }} data-testid="ecole-decompte-erreur">{erreur}</p>}
    </div>
  );
};

/* Un prof malade appelle l'école : elle note l'absence d'ici, puis choisit le remplaçant. */
const NoterAbsence = ({ profs, onFait, onErreur }) => {
  const [f, setF] = useState(null);
  if (!f) return (
    <button className="btn-ghost w-full justify-center" onClick={() => setF({ prof_id: "", du: iso(new Date()), au: "", type: "maladie", motif: "" })}
      data-testid="ecole-noter-absence">+ Noter une absence (prof malade…)</button>
  );
  return (
    <div className="p-4 space-y-2" data-testid="ecole-absence-form">
      <select className="input-field w-full" value={f.prof_id} onChange={(e) => setF({ ...f, prof_id: e.target.value })}>
        <option value="">Quel prof ?</option>
        {profs.filter((p) => p.actif !== false).map((p) => <option key={p.id} value={p.id}>{p.prenom}</option>)}
      </select>
      <div className="grid grid-cols-2 gap-2 text-[12.5px] text-[var(--muted)]">
        <label>Du<input type="date" className="input-field w-full mt-1" value={f.du} onChange={(e) => setF({ ...f, du: e.target.value })} /></label>
        <label>Au (inclus)<input type="date" className="input-field w-full mt-1" value={f.au} min={f.du} onChange={(e) => setF({ ...f, au: e.target.value })} /></label>
      </div>
      <select className="input-field w-full" value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })}>
        <option value="maladie">Maladie</option>
        <option value="vacances">Vacances</option>
        <option value="autre">Autre</option>
      </select>
      <input className="input-field w-full" placeholder="Motif (facultatif)" value={f.motif} onChange={(e) => setF({ ...f, motif: e.target.value })} />
      <div className="flex gap-2">
        <button className="btn-ghost flex-1 justify-center" onClick={() => setF(null)}>Annuler</button>
        <button className="btn-primary flex-1 justify-center" disabled={!f.prof_id || !f.du}
          onClick={async () => {
            try { await api("/profs/absences", { method: "POST", body: JSON.stringify(f) }); setF(null); onFait("Absence notée : choisis qui remplace."); }
            catch (e) { onErreur(e.message); }
          }}><Check size={15} /> Noter</button>
      </div>
    </div>
  );
};

// À valider d'abord, puis pas encore envoyés, validés, et les profs sans aucun cours à la fin.
const rang = (x) => (x.statut === "brouillon" && !x.seances ? 3 : { confirme: 0, brouillon: 1, valide: 2 }[x.statut] ?? 3);

/* Les profs : les décomptes du mois à valider, et les absences à couvrir. */
export const ProfsMobile = ({ onCompte }) => {
  // Le mois ouvert d'office, comme la console : jusqu'au 10, le mois précédent (celui que
  // les profs envoient et que l'école valide) ; ensuite le mois en cours.
  const [mois, setMois] = useState(() => (new Date().getDate() <= 10 ? decalerMois(moisCourant(), -1) : moisCourant()));
  const [decomptes, setDecomptes] = useState(null);
  const [fiches, setFiches] = useState(null);
  const [detail, setDetail] = useState(null);
  const [erreur, setErreur] = useState("");
  const [message, setMessage] = useState("");
  const charger = useCallback(() => {
    setDecomptes(null);
    api(`/profs/decomptes/${mois}`).then(setDecomptes).catch((e) => setErreur(e.message));
    api("/profs").then(setFiches).catch((e) => setErreur(e.message));
  }, [mois]);
  useEffect(charger, [charger]);
  // Après un changement : la liste, et la pastille de l'onglet (absences sans remplaçant).
  const apres = (msg) => { setErreur(""); setMessage(msg || ""); charger(); onCompte?.(); };

  if (detail) return <Decompte pid={detail} mois={mois} horsApp={decomptes?.hors_app} onRetour={() => { setDetail(null); charger(); onCompte?.(); }} />;
  const aujourdhui = iso(new Date());
  const absences = (fiches?.absences || []).filter((a) => !a.annulee_le && a.au >= aujourdhui);
  const remplacer = async (a, id) => {
    try {
      await api(`/profs/absences/${a.id}`, { method: "PUT", body: JSON.stringify({ remplacant_id: id }) });
      apres(id ? "Remplaçant désigné : il est prévenu par e-mail et voit le cours dans son application." : "Remplaçant retiré.");
    } catch (e) { setErreur(e.message); }
  };
  // Le remplaçant externe proposé par le prof (vacances) : appelé, c'est d'accord (01.11.2026).
  const confirmer = async (a, confirme) => {
    try {
      const x = await api(`/profs/absences/${a.id}/remplacant-confirme`, { method: "POST", body: JSON.stringify({ confirme }) });
      apres(x.message);
    } catch (e) { setErreur(e.message); }
  };
  const annuler = async (a) => {
    if (!window.confirm(`Annuler l'absence de ${a.prof} (${dateCourte(a.du)}${a.au !== a.du ? ` → ${dateCourte(a.au)}` : ""}) ?${a.remplacant_id ? " Son remplaçant est prévenu." : ""}`)) return;
    try {
      await api(`/profs/absences/${a.id}`, { method: "DELETE" });
      apres("Absence annulée.");
    } catch (e) { setErreur(e.message); }
  };
  return (
    <div className="space-y-5" data-testid="ecole-profs">
      {erreur && <p className="text-sm rounded-2xl px-4 py-3" style={{ background: "#FEF2F2", color: "#B91C1C" }}>{erreur}</p>}
      {message && <p className="text-sm rounded-2xl px-4 py-3" style={{ background: "#F0FDF4", color: "#15803D" }} data-testid="ecole-profs-message">{message}</p>}
      <section>
        <TitreGroupe>Absences à venir</TitreGroupe>
        <Groupe>
          {!fiches ? <p className="px-4 py-3 text-sm text-[var(--muted)]">Chargement…</p>
            : !absences.length ? <p className="px-4 py-3 text-sm text-[var(--muted)]">Aucune absence annoncée.</p>
              : absences.map((a) => (
                <div key={a.id} className="px-4 py-3 space-y-2" data-testid="ecole-absence">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-[15px] font-medium">{a.prof} <span className="font-normal text-[var(--muted)]">· {a.type}</span></div>
                      <div className="text-[12.5px] text-[var(--muted)] break-words">{dateCourte(a.du)}{a.au !== a.du ? ` → ${dateCourte(a.au)}` : ""}{a.motif ? ` · ${a.motif}` : ""}</div>
                      {/* Pour quel cours (l'absence d'un seul cours ne le disait pas). */}
                      <div className="text-[12.5px] text-[var(--muted)] break-words">{a.cours_nom ? `Seulement : ${a.cours_nom}` : "Tous ses cours"}</div>
                    </div>
                    {/* Une absence notée par erreur s'annule aussi d'ici. */}
                    <button onClick={() => annuler(a)} aria-label="Annuler cette absence" data-testid="ecole-absence-annuler"
                      className="w-10 h-10 grid place-items-center rounded-full shrink-0" style={{ background: "#F1F5F9", color: "#64748B" }}>
                      <Trash2 size={16} />
                    </button>
                  </div>
                  {a.remplacant_externe && (
                    <div className="text-[12.5px] break-words" data-testid="ecole-remplacant-externe">
                      Remplaçant·e externe : <strong>{a.remplacant_externe.prenom} {a.remplacant_externe.nom}</strong>{" · "}
                      <a href={`tel:${String(a.remplacant_externe.telephone).replace(/[^\d+]/g, "")}`} className="underline">{a.remplacant_externe.telephone}</a>
                    </div>
                  )}
                  {a.remplacant_externe && !a.remplacant_id && (a.remplacant_confirme_le ? (
                    <div className="flex items-center justify-between gap-2" data-testid="ecole-remplacant-confirme">
                      <span className="text-[12.5px] font-medium inline-flex items-center gap-1" style={{ color: "#15803D" }}>
                        <Check size={14} /> Confirmé le {new Date(a.remplacant_confirme_le).toLocaleDateString("fr-CH")}
                      </span>
                      <button onClick={() => confirmer(a, false)} className="text-[13px] min-h-[44px] px-1 text-[var(--muted)]"
                        data-testid="ecole-remplacant-deconfirmer">Annuler la confirmation</button>
                    </div>
                  ) : (
                    <button onClick={() => confirmer(a, true)} className="btn-primary w-full justify-center" data-testid="ecole-remplacant-confirmer">
                      <Check size={15} /> Remplaçant confirmé
                    </button>
                  ))}
                  <select className="input-field w-full" value={a.remplacant_id || ""} onChange={(e) => remplacer(a, e.target.value)}
                    data-testid="ecole-remplacant">
                    <option value="">Remplaçant : personne pour l'instant</option>
                    {fiches.profs.filter((p) => p.id !== a.prof_id && p.actif !== false).map((p) => <option key={p.id} value={p.id}>Remplaçant : {p.prenom}</option>)}
                  </select>
                </div>
              ))}
          {fiches && <NoterAbsence profs={fiches.profs} onFait={apres} onErreur={setErreur} />}
        </Groupe>
      </section>

      <section>
        <TitreGroupe action={
          <span className="flex items-center gap-1">
            <button onClick={() => setMois(decalerMois(mois, -1))} aria-label="Mois précédent" className="w-10 h-10 grid place-items-center rounded-full bg-white" style={{ border: "1px solid var(--border)" }}><ChevronLeft size={16} /></button>
            <button onClick={() => setMois(decalerMois(mois, 1))} aria-label="Mois suivant" className="w-10 h-10 grid place-items-center rounded-full bg-white" style={{ border: "1px solid var(--border)" }}><ChevronRight size={16} /></button>
          </span>
        }>Décomptes · {moisLisible(mois)}</TitreGroupe>
        <Groupe>
          {!decomptes ? <p className="px-4 py-3 text-sm text-[var(--muted)]">Calcul…</p>
            : !decomptes.decomptes.length ? <p className="px-4 py-3 text-sm text-[var(--muted)]">Aucun prof payé.</p>
              // Les décomptes à valider d'abord, puis ceux pas encore envoyés, puis les validés.
              : [...decomptes.decomptes].sort((a, b) => rang(a) - rang(b)).map((x) => {
                // Un mois d'avant l'application des profs : fait à la main, rien n'est attendu.
                const [lib, coul] = !x.seances && x.statut === "brouillon" && !decomptes.hors_app
                  ? ["aucun cours", "#94A3B8"] : etatDecompte(x.statut, mois, decomptes.hors_app);
                return (
                  <button key={x.prof.id} onClick={() => setDetail(x.prof.id)} data-testid={`ecole-decompte-${x.prof.id}`}
                    className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left active:bg-[#F1F5F9]">
                    <span className="min-w-0">
                      <span className="block text-[15px] font-medium">{x.prof.nom_complet || x.prof.prenom}</span>
                      <span className="flex items-center gap-2 mt-1"><Badge couleur={coul}>{lib}</Badge>
                        <span className="text-[12px] text-[var(--muted)]">{x.heures.toFixed(1)} h</span></span>
                    </span>
                    <span className="text-[15px] font-semibold tabular-nums shrink-0">{francs(x.total)}</span>
                  </button>
                );
              })}
        </Groupe>
      </section>
    </div>
  );
};
